/**
 * New Users Modal Component
 * Confirmation modal for clearing "New" tags from selected users
 */

import React from 'react';
import { Platform, View, Text, StyleSheet } from 'react-native';
import AdminModal from '../../components/AdminModal';
import AdminButton from '../../components/AdminButton';
import { AdminColors } from '../../constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import { useTheme } from '../../../../theme/useTheme';

interface NewUsersModalProps {
  visible: boolean;
  selectedCount: number;
  onClose: () => void;
  onConfirm: () => void;
}

const NewUsersModal: React.FC<NewUsersModalProps> = ({
  visible,
  selectedCount,
  onClose,
  onConfirm,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const footer = (
    <>
      <AdminButton label="Cancel" variant="ghost" onPress={onClose} />
      <AdminButton label="Clear Tags" variant="primary" onPress={handleConfirm} />
    </>
  );

  return (
    <AdminModal
      visible={visible}
      title='Clear "New" Tags'
      onClose={onClose}
      footer={footer}
      width={440}
    >
      <View style={styles.content}>
        <Text style={[styles.messageText, isDark && { color: colors.text.primary }]}>
          Are you sure you want to clear the "New" tag from{' '}
          <Text style={[styles.bold, isDark && { color: colors.text.primary }]}>{selectedCount}</Text> selected user{selectedCount !== 1 ? 's' : ''}?
        </Text>

        <Text style={[styles.warningText, isDark && { color: colors.status.warning }]}>
          This action cannot be undone.
        </Text>
      </View>
    </AdminModal>
  );
};

const styles = StyleSheet.create({
  content: {
    gap: 6,
  },
  messageText: {
    fontSize: 13,
    lineHeight: 20,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    marginBottom: 6,
  },
  bold: {
    fontWeight: '600',
    fontFamily: FontFamily.bold,
  },
  warningText: {
    fontSize: 13,
    lineHeight: 20,
    fontWeight: '500',
    color: AdminColors.textWarning,
    fontFamily: FontFamily.medium,
  },
});

export default NewUsersModal;
